import React, { useRef } from 'react';

import { cn } from '@/utils/classname';

export interface DaySelectorProps {
  selectedDays: string[]; // ['mon', 'wed', 'fri']
  onDaysChange: (days: string[]) => void;
  disabled?: boolean;
}

interface DayConfig {
  key: string;
  shortLabel: string;
  fullLabel: string;
}

const DAYS: DayConfig[] = [
  { key: 'mon', shortLabel: 'Mon', fullLabel: 'Monday' },
  { key: 'tue', shortLabel: 'Tue', fullLabel: 'Tuesday' },
  { key: 'wed', shortLabel: 'Wed', fullLabel: 'Wednesday' },
  { key: 'thu', shortLabel: 'Thu', fullLabel: 'Thursday' },
  { key: 'fri', shortLabel: 'Fri', fullLabel: 'Friday' },
  { key: 'sat', shortLabel: 'Sat', fullLabel: 'Saturday' },
  { key: 'sun', shortLabel: 'Sun', fullLabel: 'Sunday' },
];

/**
 * DaySelector - Compact pill-based day of week selector
 *
 * Replaces 7 individual checkboxes with a horizontal row of pill buttons.
 * Follows Fitts' Law with 44px minimum touch targets for better UX.
 *
 * @param selectedDays - Array of selected day keys (e.g., ['mon', 'wed', 'fri'])
 * @param onDaysChange - Callback when selection changes
 * @param disabled - Disable all buttons
 */
export function DaySelector({
  selectedDays,
  onDaysChange,
  disabled = false,
}: DaySelectorProps) {
  const buttonsRef = useRef<(HTMLButtonElement | null)[]>([]);

  const handleDayToggle = (dayKey: string) => {
    if (disabled) return;

    const isSelected = selectedDays.includes(dayKey);
    if (isSelected) {
      onDaysChange(selectedDays.filter(d => d !== dayKey));
    } else {
      onDaysChange([...selectedDays, dayKey]);
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLButtonElement>,
    index: number,
    dayKey: string
  ) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleDayToggle(dayKey);
      return;
    }

    if (e.key === 'ArrowRight') {
      e.preventDefault();
      const nextIndex = (index + 1) % DAYS.length;
      buttonsRef.current[nextIndex]?.focus();
      return;
    }

    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      const prevIndex = (index - 1 + DAYS.length) % DAYS.length;
      buttonsRef.current[prevIndex]?.focus();
      return;
    }
  };

  return (
    <div className="space-y-2">
      <label className="text-chariot-text-secondary text-sm font-medium">
        Run on these days:
      </label>
      <div
        role="group"
        aria-label="Select days of the week"
        className="flex flex-wrap gap-2"
      >
        {DAYS.map((day, index) => {
          const isSelected = selectedDays.includes(day.key);
          return (
            <button
              key={day.key}
              ref={el => {
                buttonsRef.current[index] = el;
              }}
              type="button"
              role="button"
              aria-pressed={isSelected}
              aria-label={day.fullLabel}
              disabled={disabled}
              onClick={() => handleDayToggle(day.key)}
              onKeyDown={e => handleKeyDown(e, index, day.key)}
              className={cn(
                'min-h-11 min-w-[3rem] rounded-full px-4 py-2 text-sm font-medium',
                'transition-all duration-150 ease-in-out',
                'focus:ring-2 focus:ring-offset-2 focus:outline-none',
                isSelected
                  ? 'bg-chariot-brand-primary focus:ring-chariot-brand-primary text-white'
                  : 'border-chariot-stroke bg-chariot-background-secondary text-chariot-text-primary border',
                isSelected
                  ? 'hover:bg-chariot-brand-primary/90'
                  : 'hover:bg-chariot-background-tertiary',
                disabled && 'cursor-not-allowed opacity-50'
              )}
            >
              {day.shortLabel}
            </button>
          );
        })}
      </div>
    </div>
  );
}
